import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const NOTE_TYPES = [
  {
    id: "note",
    labelKey: "gigNotesAdmin.types.note.label",
    labelDefault: "Muistiinpano",
    descriptionKey: "gigNotesAdmin.types.note.description",
    descriptionDefault: "Keikkaan, asiakkaaseen tai settiin liittyvä huomio.",
  },
  {
    id: "reminder",
    labelKey: "gigNotesAdmin.types.reminder.label",
    labelDefault: "Muistutus",
    descriptionKey: "gigNotesAdmin.types.reminder.description",
    descriptionDefault: "Nopea muistettava asia lavalle tai ennen keikkaa.",
  },
  {
    id: "speech",
    labelKey: "gigNotesAdmin.types.speech.label",
    labelDefault: "Välispiikki",
    descriptionKey: "gigNotesAdmin.types.speech.description",
    descriptionDefault: "Valmis spiikki tai juonto eri tilanteisiin.",
  },
];

const OCCASIONS = [
  {
    value: "häät",
    key: "wedding",
    defaultLabel: "Häät",
  },
  {
    value: "laiva",
    key: "ship",
    defaultLabel: "Laiva",
  },
  {
    value: "live",
    key: "live",
    defaultLabel: "Live",
  },
  {
    value: "muu",
    key: "other",
    defaultLabel: "Muu",
  },
  {
    value: "palvelutalo",
    key: "careHome",
    defaultLabel: "Palvelutalo",
  },
  {
    value: "pikkujoulut",
    key: "christmasParty",
    defaultLabel: "Pikkujoulut",
  },
  {
    value: "pubi",
    key: "pub",
    defaultLabel: "Pubi",
  },
  {
    value: "syntymäpäivät",
    key: "birthday",
    defaultLabel: "Syntymäpäivät",
  },
  {
    value: "yleinen",
    key: "general",
    defaultLabel: "Yleinen",
  },
  {
    value: "yritysjuhlat",
    key: "corporateParty",
    defaultLabel: "Yritysjuhlat",
  },
];

function getApiBaseUrl() {
  return (
    window.PARANOIDI_API_BASE_URL ||
    process.env.REACT_APP_PARANOIDI_API_BASE_URL ||
    "http://localhost:4000/api"
  ).replace(/\/$/, "");
}

function getGigNotesApiUrl(performerSlug, noteId = "") {
  const cleanSlug = encodeURIComponent(performerSlug || "default");
  const cleanNoteId = noteId ? `/${encodeURIComponent(noteId)}` : "";

  return `${getApiBaseUrl()}/admin/performers/${cleanSlug}/gig-notes${cleanNoteId}`;
}

function formatDate(value, locale = "fi-FI") {
  if (!value) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

export default function GigNotesAdmin({ performerSlug }) {
  const { t, i18n } = useTranslation("admin");

  const adminLocale =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en")
      ? "en-GB"
      : "fi-FI";

  function createLocalizedRequestError(data, fallbackMessage) {
    const error = new Error(fallbackMessage);

    error.code = data?.code || "";
    error.payload = data || {};
    error.backendMessage = String(data?.error || "").trim();

    return error;
  }

  function getLocalizedErrorMessage(error, fallbackMessage) {
    const message = String(error?.message || "").trim();

    return error?.backendMessage !== undefined && message
      ? message
      : fallbackMessage;
  }

  function getTypeLabel(type) {
    const item =
      NOTE_TYPES.find((noteType) => noteType.id === type) ||
      NOTE_TYPES.find((noteType) => noteType.id === "note");

    return t(item.labelKey, {
      defaultValue: item.labelDefault,
    });
  }

  function formatOccasionLabel(value) {
    const item = OCCASIONS.find(
      (occasionItem) => occasionItem.value === String(value || "").trim()
    );

    if (item) {
      return t(`gigNotesAdmin.occasions.${item.key}`, {
        defaultValue: item.defaultLabel,
      });
    }

    const text = String(value || "").trim();

    if (!text) {
      return "";
    }

    return (
      text.charAt(0).toLocaleUpperCase(adminLocale) +
      text.slice(1)
    );
  }

  const noteFilters = [
    [
      "all",
      t("gigNotesAdmin.saved.filters.all", {
        defaultValue: "Kaikki",
      }),
    ],
    [
      "speech",
      t("gigNotesAdmin.saved.filters.speech", {
        defaultValue: "Välispiikit",
      }),
    ],
    [
      "note",
      t("gigNotesAdmin.saved.filters.note", {
        defaultValue: "Muistiinpanot",
      }),
    ],
    [
      "reminder",
      t("gigNotesAdmin.saved.filters.reminder", {
        defaultValue: "Muistutukset",
      }),
    ],
  ];

  const [items, setItems] = useState([]);
  const [selectedType, setSelectedType] = useState("speech");
  const [title, setTitle] = useState("");
  const [occasion, setOccasion] = useState("yleinen");
  const [content, setContent] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadGigNotes() {
        if (!performerSlug) {
        setItems([]);
        return;
        }

        setIsLoading(true);
        setMessage("");

        try {
        const response = await fetchWithCredentials(getGigNotesApiUrl(performerSlug));
        const data = await response.json();

        if (!response.ok || !data.ok) {
            throw createLocalizedRequestError(
              data,
              t("gigNotesAdmin.messages.loadFailed", {
                defaultValue: "Keikkamuistin haku epäonnistui.",
              })
            );
        }

        if (isMounted) {
            setItems(Array.isArray(data.items) ? data.items : []);
        }
        } catch (error) {
        if (isMounted) {
            setItems([]);
            setMessage(
              getLocalizedErrorMessage(
                error,
                t("gigNotesAdmin.messages.loadFailed", {
                  defaultValue: "Keikkamuistin haku epäonnistui.",
                })
              )
            );
        }
        } finally {
        if (isMounted) {
            setIsLoading(false);
        }
        }
    }

    loadGigNotes();

    return () => {
        isMounted = false;
    };
  }, [performerSlug, t]);

  const filteredItems = items
    .filter((item) => activeFilter === "all" || item.type === activeFilter)
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) {
        return a.isPinned ? -1 : 1;
      }

      const titleCompare = String(a.title || "").localeCompare(
        String(b.title || ""),
        adminLocale,
        { sensitivity: "base" }
      );

      if (titleCompare !== 0) {
        return titleCompare;
      }

      return (
        new Date(b.updatedAt || b.createdAt) -
        new Date(a.updatedAt || a.createdAt)
      );
    });

  function resetForm() {
    setSelectedType("speech");
    setTitle("");
    setOccasion("yleinen");
    setContent("");
    setIsPinned(false);
    setEditingId("");
    setIsFormOpen(false);
  }

  function startCreate() {
    setSelectedType("speech");
    setTitle("");
    setOccasion("yleinen");
    setContent("");
    setIsPinned(false);
    setEditingId("");
    setMessage("");
    setIsFormOpen(true);
  }

    async function handleSubmit(event) {
    event.preventDefault();

    const cleanTitle = title.trim();
    const cleanContent = content.trim();

    if (!cleanTitle || !cleanContent) {
        setMessage(
        t("gigNotesAdmin.messages.requiredFields", {
          defaultValue: "Anna otsikko ja sisältö.",
        })
      );
        return;
    }

    const payload = {
        type: selectedType,
        title: cleanTitle,
        occasion,
        content: cleanContent,
        isPinned,
    };

    setIsSaving(true);
    setMessage("");

    try {
        const response = await fetchWithCredentials(getGigNotesApiUrl(performerSlug, editingId), {
        method: editingId ? "PUT" : "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
        throw createLocalizedRequestError(
          data,
          t("gigNotesAdmin.messages.saveFailed", {
            defaultValue: "Keikkamuistin tallennus epäonnistui.",
          })
        );
        }

        if (editingId) {
        setItems((currentItems) =>
            currentItems.map((item) =>
            item.id === editingId || String(item.id) === String(editingId)
                ? data.item
                : item
            )
        );

        setMessage(
          t("gigNotesAdmin.messages.updated", {
            defaultValue: "Keikkamuisti päivitetty.",
          })
        );
        resetForm();
        return;
        }

        setItems((currentItems) => [data.item, ...currentItems]);
        setMessage(
        t("gigNotesAdmin.messages.saved", {
          defaultValue: "Keikkamuisti tallennettu.",
        })
      );
        resetForm();
    } catch (error) {
        setMessage(
          getLocalizedErrorMessage(
            error,
            t("gigNotesAdmin.messages.saveFailed", {
              defaultValue: "Keikkamuistin tallennus epäonnistui.",
            })
          )
        );
    } finally {
        setIsSaving(false);
    }
    }

  function editItem(item) {
    setEditingId(item.id);
    setSelectedType(item.type || "note");
    setTitle(item.title || "");
    setOccasion(item.occasion || "yleinen");
    setContent(item.content || "");
    setIsPinned(Boolean(item.isPinned));
    setMessage("");
    setIsFormOpen(true);
    window.requestAnimationFrame(() => {
      document
        .getElementById("gig-note-form")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    });
  }

    async function deleteItem(itemId) {
    setIsSaving(true);
    setMessage("");

    try {
        const response = await fetchWithCredentials(getGigNotesApiUrl(performerSlug, itemId), {
        method: "DELETE",
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
        throw createLocalizedRequestError(
          data,
          t("gigNotesAdmin.messages.deleteFailed", {
            defaultValue: "Keikkamuistin poisto epäonnistui.",
          })
        );
        }

        setItems((currentItems) =>
        currentItems.filter((item) => String(item.id) !== String(itemId))
        );

        if (String(editingId) === String(itemId)) {
        resetForm();
        }

        setDeleteConfirmNote(null);
        setMessage(
        t("gigNotesAdmin.messages.deleted", {
          defaultValue: "Keikkamuisti poistettu.",
        })
      );
    } catch (error) {
        setMessage(
          getLocalizedErrorMessage(
            error,
            t("gigNotesAdmin.messages.deleteFailed", {
              defaultValue: "Keikkamuistin poisto epäonnistui.",
            })
          )
        );
    } finally {
        setIsSaving(false);
    }
    }

    async function togglePinned(itemId) {
    const item = items.find(
        (currentItem) => String(currentItem.id) === String(itemId)
    );

    if (!item) {
        return;
    }

    const payload = {
        type: item.type || "note",
        occasion: item.occasion || "yleinen",
        title: item.title || "",
        content: item.content || "",
        isPinned: !item.isPinned,
    };

    setIsSaving(true);
    setMessage("");

    try {
        const response = await fetchWithCredentials(getGigNotesApiUrl(performerSlug, itemId), {
        method: "PUT",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
        throw createLocalizedRequestError(
          data,
          t("gigNotesAdmin.messages.pinUpdateFailed", {
            defaultValue: "Kiinnityksen päivitys epäonnistui.",
          })
        );
        }

        setItems((currentItems) =>
        currentItems.map((currentItem) =>
            String(currentItem.id) === String(itemId) ? data.item : currentItem
        )
        );

        setMessage(
        data.item.isPinned
          ? t("gigNotesAdmin.messages.pinned", {
              defaultValue: "Keikkamuisti kiinnitetty.",
            })
          : t("gigNotesAdmin.messages.unpinned", {
              defaultValue: "Kiinnitys poistettu.",
            })
      );
    } catch (error) {
        setMessage(
          getLocalizedErrorMessage(
            error,
            t("gigNotesAdmin.messages.pinUpdateFailed", {
              defaultValue: "Kiinnityksen päivitys epäonnistui.",
            })
          )
        );
    } finally {
        setIsSaving(false);
    }
    }

  async function copyItem(item) {
    const text = [item.title, "", item.content].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setMessage(
        t("gigNotesAdmin.messages.copied", {
          defaultValue: "Kopioitu leikepöydälle.",
        })
      );
    } catch {
      setMessage(
        t("gigNotesAdmin.messages.copyFailed", {
          defaultValue:
            "Kopiointi ei onnistunut. Voit kopioida tekstin käsin.",
        })
      );
    }
  }

  return (
    <section className="-mx-3 mt-8 border-y border-[#5a3824]/15 bg-[#fff8ef]/85 px-3 py-6 shadow-none theme-dark:border-[#ef4444]/20 theme-dark:bg-white/[0.04] sm:mx-0 sm:rounded-[2rem] sm:border sm:p-7 sm:shadow-[0_18px_50px_rgba(58,43,24,0.10)]">
        {deleteConfirmNote &&
  createPortal(
    <div className="fixed inset-0 z-[100000] grid place-items-center bg-black/55 px-4 backdrop-blur-sm">
      <div className="relative z-[100001] w-full max-w-md rounded-[2rem] border border-[#ef4444]/25 bg-[#050605] p-6 shadow-2xl shadow-black/40">
        <p className="text-xs font-black uppercase tracking-[0.24em] text-[#ef4444]">
          {t("gigNotesAdmin.delete.confirmLabel", {
            defaultValue: "Vahvista poisto",
          })}
        </p>

        <h3 className="mt-3 text-2xl font-black tracking-[-0.04em] text-white">
          {t("gigNotesAdmin.delete.title", {
            defaultValue: "Poistetaanko keikkamuisti?",
          })}
        </h3>

        <p className="mt-3 text-sm leading-7 text-zinc-400">
          {t("gigNotesAdmin.delete.description", {
            defaultValue:
              "Tätä toimintoa ei voi perua. Poistettava merkintä:",
          })}
        </p>

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
          <p className="text-sm font-black text-white">
            {deleteConfirmNote.title}
          </p>

          {deleteConfirmNote.content && (
            <p className="mt-1 line-clamp-2 text-xs font-semibold leading-5 text-zinc-400">
              {deleteConfirmNote.content}
            </p>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setDeleteConfirmNote(null)}
            className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm font-black text-zinc-300 transition hover:bg-white/10"
          >
            {t("gigNotesAdmin.delete.cancel", {
              defaultValue: "Peruuta",
            })}
          </button>

          <button
            type="button"
            onClick={() => deleteItem(deleteConfirmNote.id)}
            disabled={isSaving}
            className="rounded-full border border-[#ef4444]/30 bg-[#ef4444]/10 px-5 py-2.5 text-sm font-black text-[#fecaca] transition hover:bg-[#ef4444]/15 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving
              ? t("gigNotesAdmin.delete.deleting", {
                  defaultValue: "Poistetaan...",
                })
              : t("gigNotesAdmin.delete.deleteButton", {
                  defaultValue: "Poista keikkamuisti",
                })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}
  
  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div className="max-w-3xl">
      <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
        {t("gigNotesAdmin.header.eyebrow", {
          defaultValue: "Keikkamuisti",
        })}
      </p>

      <h2 className="mt-3 text-3xl font-semibold tracking-[-0.045em] text-[#241a16] theme-dark:text-white sm:text-4xl">
        {t("gigNotesAdmin.header.title", {
          defaultValue: "Spiikit, muistutukset ja muistiinpanot",
        })}
      </h2>

      <p className="mt-4 text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
        {t("gigNotesAdmin.header.description", {
          defaultValue:
            "Tallenna keikalla tarvittavat jutut ja liitä niitä tarvittaessa settilistan biiseihin.",
        })}
      </p>
    </div>

    {!isFormOpen && (
      <button
        type="button"
        onClick={startCreate}
        className="inline-flex shrink-0 justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:bg-[#ef4444]/15"
      >
        {t("gigNotesAdmin.header.addButton", {
          defaultValue: "+ Lisää keikkamuisti",
        })}
      </button>
    )}
  </div>

    {isFormOpen && (
      <form
        id="gig-note-form"
        onSubmit={handleSubmit}
        className="mt-6 w-full rounded-[1.5rem] border border-[#5a3824]/10 bg-white/45 p-4 theme-dark:border-white/10 theme-dark:bg-black/20 sm:p-5"
      >
        <div className="flex flex-col gap-2 border-b border-[#5a3824]/10 pb-4 theme-dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
              {editingId
                ? t("gigNotesAdmin.form.editEyebrow", {
                    defaultValue: "Muokkaa",
                  })
                : t("gigNotesAdmin.form.newEyebrow", {
                    defaultValue: "Uusi merkintä",
                  })}
            </p>

            <h3 className="mt-1 text-xl font-semibold tracking-[-0.03em] text-[#241a16] theme-dark:text-white">
              {editingId
                ? t("gigNotesAdmin.form.editTitle", {
                    defaultValue: "Muokkaa keikkamuistia",
                  })
                : t("gigNotesAdmin.form.addTitle", {
                    defaultValue: "Lisää keikkamuisti",
                  })}
            </h3>
          </div>

          <button
            type="button"
            onClick={resetForm}
            className="self-start rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10 sm:self-auto"
          >
            {t("gigNotesAdmin.form.close", {
              defaultValue: "Sulje",
            })}
          </button>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
            {t("gigNotesAdmin.form.type", {
              defaultValue: "Tyyppi",
            })}
            <select
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              className="rounded-2xl border border-[#5a3824]/15 bg-white/90 px-4 py-3 text-sm font-semibold outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
            >
              {NOTE_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {getTypeLabel(type.id)}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
            {t("gigNotesAdmin.form.occasion", {
              defaultValue: "Tilanne",
            })}
            <select
              value={occasion}
              onChange={(event) => setOccasion(event.target.value)}
              className="rounded-2xl border border-[#5a3824]/15 bg-white/90 px-4 py-3 text-sm font-semibold outline-none [color-scheme:light] theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:[color-scheme:dark]"
            >
              {OCCASIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {formatOccasionLabel(item.value)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-4 grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
          {t("gigNotesAdmin.form.title", {
            defaultValue: "Otsikko",
          })}
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="rounded-2xl border border-[#5a3824]/15 bg-white/90 px-4 py-3 text-sm font-semibold outline-none placeholder:text-[#8a5a35]/50 theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600"
            placeholder={t("gigNotesAdmin.form.titlePlaceholder", {
              defaultValue: "Esim. Ennen yhteislaulua",
            })}
          />
        </label>

        <label className="mt-4 grid gap-2 text-sm font-semibold text-[#241a16] theme-dark:text-white">
          {t("gigNotesAdmin.form.content", {
            defaultValue: "Sisältö",
          })}
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={5}
            className="resize-none rounded-2xl border border-[#5a3824]/15 bg-white/90 px-4 py-3 text-sm font-semibold leading-7 outline-none placeholder:text-[#8a5a35]/50 theme-dark:border-white/10 theme-dark:bg-[#050605] theme-dark:text-white theme-dark:placeholder:text-zinc-600"
            placeholder={t("gigNotesAdmin.form.contentPlaceholder", {
              defaultValue:
                "Kirjoita välispiikki, muistettava asia tai lavalla näkyvä huomio...",
            })}
          />
        </label>

        <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/70 px-4 py-3 text-sm font-semibold text-[#241a16] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-white">
          <span>
            <span className="block font-black">
              {t("gigNotesAdmin.form.pinTitle", {
                defaultValue: "Kiinnitä listan alkuun",
              })}
            </span>
            <span className="mt-1 block text-xs font-medium text-[#5a3824] theme-dark:text-zinc-400">
              {t("gigNotesAdmin.form.pinDescription", {
                defaultValue: "Tärkeät merkinnät pysyvät ensimmäisinä.",
              })}
            </span>
          </span>

          <span className="relative inline-flex h-7 w-12 shrink-0 items-center">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(event) => setIsPinned(event.target.checked)}
              className="peer sr-only"
            />

            <span className="absolute inset-0 rounded-full bg-[#5a3824]/15 transition peer-checked:bg-[#5a3824] theme-dark:bg-white/10 theme-dark:peer-checked:bg-[#ef4444]" />

            <span className="relative ml-1 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
          </span>
        </label>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex w-full justify-center rounded-full border border-[#5a3824]/20 bg-[#5a3824] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6b4229] disabled:cursor-not-allowed disabled:opacity-60 theme-dark:border-[#ef4444]/35 theme-dark:bg-[#ef4444]/10 theme-dark:text-[#fecaca] theme-dark:hover:bg-[#ef4444]/15 sm:w-auto"
          >
            {isSaving
              ? t("gigNotesAdmin.form.saving", {
                  defaultValue: "Tallennetaan...",
                })
              : editingId
                ? t("gigNotesAdmin.form.saveChanges", {
                    defaultValue: "Tallenna muutokset",
                  })
                : t("gigNotesAdmin.form.addButton", {
                    defaultValue: "Lisää keikkamuisti",
                  })}
          </button>
        </div>
      </form>
    )}

    {message && (
      <p className="mt-5 rounded-2xl border border-[#5a3824]/10 bg-[#fff8ef]/75 px-4 py-3 text-sm font-semibold text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
        {message}
      </p>
    )}

      <div className="mt-8 border-t border-[#5a3824]/10 pt-6 theme-dark:border-white/10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-semibold tracking-[-0.04em] text-[#241a16] theme-dark:text-white">
              {t("gigNotesAdmin.saved.title", {
                defaultValue: "Tallennetut",
              })}
            </h3>

              {items.length > 0 && (
                <p className="mt-1 text-sm leading-6 text-[#5a3824] theme-dark:text-zinc-400">
                  {t("gigNotesAdmin.saved.count", {
                    count: items.length,
                    defaultValue: "{{count}} tallennettua merkintää.",
                  })}
                </p>
              )}
          </div>
            {items.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                {noteFilters.map(([filter, label]) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setActiveFilter(filter)}
                    className={[
                      "rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.12em] transition",
                      activeFilter === filter
                        ? "bg-[#5a3824] text-white theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]"
                        : "border border-[#5a3824]/10 bg-white/60 text-[#5a3824] hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
        </div>

        {isLoading ? (
            <div className="mt-5 rounded-[1.5rem] border border-dashed border-[#5a3824]/20 bg-white/35 p-6 text-sm leading-7 text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
                {t("gigNotesAdmin.saved.loading", {
                  defaultValue: "Ladataan keikkamuistia...",
                })}
            </div>
            ) : filteredItems.length === 0 ? (
          <div className="mt-5 rounded-[1.5rem] border border-dashed border-[#5a3824]/20 bg-white/35 p-6 text-sm leading-7 text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.03] theme-dark:text-zinc-400">
            {t("gigNotesAdmin.saved.empty", {
              defaultValue:
                "Ei vielä tallennettuja keikkamuistiinpanoja.",
            })}
          </div>
        ) : (
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {filteredItems.map((item) => (
              <article
                key={item.id}
                className="rounded-[1.5rem] border border-[#5a3824]/10 bg-white/65 p-4 theme-dark:border-white/10 theme-dark:bg-black/20"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#5a3824] px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.14em] text-white theme-dark:bg-[#ef4444]/15 theme-dark:text-[#fecaca]">
                        {getTypeLabel(item.type)}
                      </span>

                      <span className="rounded-full border border-[#5a3824]/10 bg-[#fff8ef]/70 px-3 py-1 text-[0.65rem] font-black uppercase tracking-[0.14em] text-[#5a3824] theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300">
                        {formatOccasionLabel(item.occasion || "yleinen")}
                      </span>

                      {item.isPinned && (
                        <span className="inline-flex items-center gap-1.5 px-1 text-[0.65rem] font-black uppercase tracking-[0.14em] text-red-700 theme-dark:text-red-400">
                          <span aria-hidden="true">📌</span>
                          {t("gigNotesAdmin.saved.pinned", {
                            defaultValue: "Kiinnitetty",
                          })}
                        </span>
                      )}
                    </div>

                    <h4 className="mt-3 break-words text-xl font-semibold tracking-[-0.035em] text-[#241a16] theme-dark:text-white">
                      {item.title}
                    </h4>

                    <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-7 text-[#5a3824] theme-dark:text-zinc-300">
                      {item.content}
                    </p>

                    <p className="mt-3 text-xs font-medium text-[#8a5a35] theme-dark:text-zinc-500">
                      {t("gigNotesAdmin.saved.updated", {
                        date: formatDate(
                          item.updatedAt || item.createdAt,
                          adminLocale
                        ),
                        defaultValue: "Päivitetty {{date}}",
                      })}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => togglePinned(item.id)}
                    className="rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                  >
                    {item.isPinned
                      ? t("gigNotesAdmin.saved.unpin", {
                          defaultValue: "Poista kiinnitys",
                        })
                      : t("gigNotesAdmin.saved.pin", {
                          defaultValue: "Kiinnitä",
                        })}
                  </button>

                  <button
                    type="button"
                    onClick={() => copyItem(item)}
                    className="rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                  >
                    {t("gigNotesAdmin.saved.copy", {
                      defaultValue: "Kopioi",
                    })}
                  </button>

                  <button
                    type="button"
                    onClick={() => editItem(item)}
                    className="rounded-full border border-[#5a3824]/15 bg-white/70 px-4 py-2 text-xs font-semibold text-[#5a3824] transition hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.04] theme-dark:text-zinc-300 theme-dark:hover:bg-white/10"
                  >
                    {t("gigNotesAdmin.saved.edit", {
                      defaultValue: "Muokkaa",
                    })}
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeleteConfirmNote(item)}
                    className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200 theme-dark:hover:bg-red-500/15"
                  >
                    {t("gigNotesAdmin.saved.delete", {
                      defaultValue: "Poista",
                    })}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/*
 * Admin-pyyntöjen autentikointi perustuu palvelimen HttpOnly-session-cookieen.
 * Lähetetään cookie mukana myös localhostin frontend -> backend -pyynnöissä.
 */
function fetchWithCredentials(input, init = {}) {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}